const EARTH_RADIUS_KM = 6371;
const SECONDARY_ADMINISTRATIVE_AREA_PATTERN = /\b(county|parish|borough|municipality|district)\b/i;

const readLocationText = (value) => (typeof value === 'string' ? value.trim() : '');

const normalizeLocationComparison = (value) => readLocationText(value)
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const readLocationCoordinate = (location, keys) => {
  for (const key of keys) {
    const coordinate = Number.parseFloat(location?.[key]);
    if (Number.isFinite(coordinate)) {
      return coordinate;
    }
  }
  return null;
};

export const formatLocationDisplay = (location, explicitDisplay) => {
  if (typeof explicitDisplay === 'string' && explicitDisplay.trim().length > 0) {
    return explicitDisplay.trim();
  }
  if (!location || typeof location !== 'object') {
    return '';
  }
  if (typeof location.formatted === 'string') {
    const formatted = location.formatted.trim();
    if (formatted.length > 0) {
      return formatted;
    }
  }
  const parts = [location.city, location.stateOrRegion, location.country]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0);
  return parts.join(', ');
};

export const normalizeCountryCode = (code) => {
  if (typeof code !== 'string') {
    return '';
  }
  const trimmed = code.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(trimmed) ? trimmed : '';
};

export const DEFAULT_LOCATION_COUNTRY_OPTION = Object.freeze({
  code: 'US',
  name: 'United States',
});

export const SUPPORTED_LOCATION_COUNTRY_OPTIONS = Object.freeze([
  DEFAULT_LOCATION_COUNTRY_OPTION,
]);

const US_STATE_ENTRIES = Object.freeze([
  ['alabama', 'AL'],
  ['alaska', 'AK'],
  ['arizona', 'AZ'],
  ['arkansas', 'AR'],
  ['california', 'CA'],
  ['colorado', 'CO'],
  ['connecticut', 'CT'],
  ['delaware', 'DE'],
  ['district of columbia', 'DC'],
  ['florida', 'FL'],
  ['georgia', 'GA'],
  ['hawaii', 'HI'],
  ['idaho', 'ID'],
  ['illinois', 'IL'],
  ['indiana', 'IN'],
  ['iowa', 'IA'],
  ['kansas', 'KS'],
  ['kentucky', 'KY'],
  ['louisiana', 'LA'],
  ['maine', 'ME'],
  ['maryland', 'MD'],
  ['massachusetts', 'MA'],
  ['michigan', 'MI'],
  ['minnesota', 'MN'],
  ['mississippi', 'MS'],
  ['missouri', 'MO'],
  ['montana', 'MT'],
  ['nebraska', 'NE'],
  ['nevada', 'NV'],
  ['new hampshire', 'NH'],
  ['new jersey', 'NJ'],
  ['new mexico', 'NM'],
  ['new york', 'NY'],
  ['north carolina', 'NC'],
  ['north dakota', 'ND'],
  ['ohio', 'OH'],
  ['oklahoma', 'OK'],
  ['oregon', 'OR'],
  ['pennsylvania', 'PA'],
  ['rhode island', 'RI'],
  ['south carolina', 'SC'],
  ['south dakota', 'SD'],
  ['tennessee', 'TN'],
  ['texas', 'TX'],
  ['utah', 'UT'],
  ['vermont', 'VT'],
  ['virginia', 'VA'],
  ['washington', 'WA'],
  ['west virginia', 'WV'],
  ['wisconsin', 'WI'],
  ['wyoming', 'WY'],
]);

const normalizeAliasTerm = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');

const US_STATE_NAME_TO_CODE = new Map(US_STATE_ENTRIES);
const US_STATE_CODE_TO_NAME = new Map(US_STATE_ENTRIES.map(([name, code]) => [code, name]));

const SUPPORTED_LOCATION_COUNTRY_ALIASES = new Set([
  DEFAULT_LOCATION_COUNTRY_OPTION.name.toLowerCase(),
  'usa',
  'u.s.',
  'u.s.a.',
  'united states of america',
]);

export const isSupportedLocationCountry = (location = {}) => {
  const normalizedCode = normalizeCountryCode(location?.countryCode ?? '');
  if (normalizedCode) {
    return normalizedCode === DEFAULT_LOCATION_COUNTRY_OPTION.code;
  }

  const normalizedCountry = typeof location?.country === 'string'
    ? location.country.trim().toLowerCase()
    : '';
  if (!normalizedCountry) {
    return true;
  }

  return SUPPORTED_LOCATION_COUNTRY_ALIASES.has(normalizedCountry);
};

export const resolveUsStateCode = (value) => {
  const normalized = normalizeAliasTerm(value);
  if (!normalized) {
    return null;
  }
  const compactCode = normalized.replace(/[^a-z]/g, '').toUpperCase();
  if (US_STATE_CODE_TO_NAME.has(compactCode)) {
    return compactCode;
  }
  return US_STATE_NAME_TO_CODE.get(normalized) || null;
};

export const normalizeFirstLevelRegionName = (value) => {
  const regionName = readLocationText(value);
  if (!regionName || resolveUsStateCode(regionName)) {
    return regionName;
  }
  return SECONDARY_ADMINISTRATIVE_AREA_PATTERN.test(regionName) ? '' : regionName;
};

export const calculateLocationDistanceKm = (origin, destination) => {
  const originLatitude = readLocationCoordinate(origin, ['latitude', 'lat']);
  const originLongitude = readLocationCoordinate(origin, ['longitude', 'lng', 'lon']);
  const destinationLatitude = readLocationCoordinate(destination, ['latitude', 'lat']);
  const destinationLongitude = readLocationCoordinate(destination, ['longitude', 'lng', 'lon']);
  if ([originLatitude, originLongitude, destinationLatitude, destinationLongitude]
    .some((coordinate) => coordinate === null)) {
    return Number.POSITIVE_INFINITY;
  }

  const toRadians = (degrees) => degrees * (Math.PI / 180);
  const latitudeDelta = toRadians(destinationLatitude - originLatitude);
  const longitudeDelta = toRadians(destinationLongitude - originLongitude);
  const originLatitudeRadians = toRadians(originLatitude);
  const destinationLatitudeRadians = toRadians(destinationLatitude);
  const haversine = Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(originLatitudeRadians) * Math.cos(destinationLatitudeRadians)
    * Math.sin(longitudeDelta / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(haversine)));
};

export const hasConflictingAdministrativeAreas = (currentLocation = {}, resolvedLocation = {}) => {
  const currentCountryCode = normalizeCountryCode(currentLocation.countryCode || '');
  const resolvedCountryCode = normalizeCountryCode(resolvedLocation.countryCode || '');
  if (currentCountryCode && resolvedCountryCode) {
    if (currentCountryCode !== resolvedCountryCode) {
      return true;
    }
  } else {
    const currentCountry = normalizeLocationComparison(currentLocation.country ?? currentLocation.countryName);
    const resolvedCountry = normalizeLocationComparison(resolvedLocation.country ?? resolvedLocation.countryName);
    if (currentCountry && resolvedCountry && currentCountry !== resolvedCountry) {
      return true;
    }
  }

  const currentCity = normalizeLocationComparison(currentLocation.city ?? currentLocation.cityName);
  const resolvedCity = normalizeLocationComparison(resolvedLocation.city ?? resolvedLocation.cityName);
  if (currentCity && resolvedCity && currentCity !== resolvedCity) {
    return true;
  }

  const currentRegion = normalizeFirstLevelRegionName(
    currentLocation.stateOrRegion ?? currentLocation.regionName,
  );
  const resolvedRegion = normalizeFirstLevelRegionName(
    resolvedLocation.stateOrRegion ?? resolvedLocation.regionName,
  );
  const currentRegionCode = resolveUsStateCode(currentRegion);
  const resolvedRegionCode = resolveUsStateCode(resolvedRegion);
  if (currentRegionCode && resolvedRegionCode) {
    return currentRegionCode !== resolvedRegionCode;
  }

  const normalizedCurrentRegion = normalizeLocationComparison(currentRegion);
  const normalizedResolvedRegion = normalizeLocationComparison(resolvedRegion);
  return Boolean(
    normalizedCurrentRegion
    && normalizedResolvedRegion
    && normalizedCurrentRegion !== normalizedResolvedRegion
  );
};

export const buildUsStateAliases = (value) => {
  const aliases = new Set();
  const normalized = normalizeAliasTerm(value);
  if (normalized) {
    aliases.add(normalized);
  }

  const stateCode = resolveUsStateCode(value);
  if (stateCode) {
    aliases.add(stateCode.toLowerCase());
    const stateName = US_STATE_CODE_TO_NAME.get(stateCode);
    if (stateName) {
      aliases.add(stateName);
    }
  }

  return [...aliases];
};

export const buildSupportedCountryAliases = (location = {}) => {
  const countryCode = normalizeCountryCode(location.countryCode || location.code || '');
  const countryName = normalizeAliasTerm(location.countryName || location.country || location.name);
  if (countryCode === DEFAULT_LOCATION_COUNTRY_OPTION.code || SUPPORTED_LOCATION_COUNTRY_ALIASES.has(countryName)) {
    return [
      DEFAULT_LOCATION_COUNTRY_OPTION.name.toLowerCase(),
      DEFAULT_LOCATION_COUNTRY_OPTION.code.toLowerCase(),
      ...SUPPORTED_LOCATION_COUNTRY_ALIASES,
    ];
  }

  return [countryName, countryCode.toLowerCase()].filter(Boolean);
};

export const buildDisplayFromParts = ({ city, stateOrRegion, country }) => {
  const parts = [city, stateOrRegion, country]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter((part) => part.length > 0);
  return parts.join(', ');
};

export const toAddressSelection = (location = {}, { primaryLabel = '' } = {}) => {
  const latitude = readLocationCoordinate(location, ['latitude', 'lat']);
  const longitude = readLocationCoordinate(location, ['longitude', 'lng', 'lon']);
  const addressLine = readLocationText(location.addressLine);
  const cityName = readLocationText(location.city ?? location.cityName);
  const regionName = readLocationText(location.stateOrRegion ?? location.regionName);
  const countryName = readLocationText(location.country ?? location.countryName);
  const label = [
    readLocationText(primaryLabel) || addressLine,
    cityName,
    regionName,
    countryName,
  ].filter(Boolean).join(', ');

  if (!label || latitude === null || longitude === null) {
    return null;
  }

  return {
    label,
    addressLine,
    cityName,
    regionName,
    countryName,
    countryCode: readLocationText(location.countryCode),
    latitude,
    longitude,
  };
};

const FALLBACK_COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'BR', name: 'Brazil' },
  { code: 'JP', name: 'Japan' }
];

export const buildCountryOptions = (locale = 'en') => {
  try {
    if (typeof Intl === 'undefined' || typeof Intl.DisplayNames === 'undefined') {
      return FALLBACK_COUNTRIES;
    }
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    let regions = [];
    if (typeof Intl.supportedValuesOf === 'function') {
      try {
        regions = Intl.supportedValuesOf('region')
          .filter((code) => /^[A-Z]{2}$/.test(code));
      } catch (_) {
        regions = FALLBACK_COUNTRIES.map((entry) => entry.code);
      }
    } else {
      regions = FALLBACK_COUNTRIES.map((entry) => entry.code);
    }
    const unique = new Set();
    const options = regions.map((code) => {
      const name = displayNames.of(code);
      if (!name || unique.has(code)) {
        return null;
      }
      unique.add(code);
      return { code, name };
    }).filter(Boolean);
    if (options.length === 0) {
      return FALLBACK_COUNTRIES;
    }
    return options.sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.warn('Failed to derive country list, falling back to static set:', error);
    return FALLBACK_COUNTRIES;
  }
};
